import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { getDateRange, getFaaliyetDateRange } from './utils/calculations';
import type { DateFilterType, FaaliyetFilterType } from './utils/calculations';
import { useTieredQueries } from './hooks/useTieredQueries';
import { useBilancoData } from './hooks/useBilancoData';
import { BilancoSection } from './components/BilancoSection';
import { ChartsSection } from './components/ChartsSection';
import { FaaliyetSection } from './components/FaaliyetSection';
import { AgentStatusBanner } from '../../components/AgentStatusBanner';

export function Dashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dateFilter, setDateFilter] = useState<DateFilterType>('last1week');
  const [faaliyetFilter, setFaaliyetFilter] = useState<FaaliyetFilterType>('thisWeek');

  const dateRange = useMemo(() => getDateRange(dateFilter), [dateFilter]);
  const faaliyetDateRange = useMemo(() => getFaaliyetDateRange(faaliyetFilter), [faaliyetFilter]);

  // Kademeli sorgu sistemi - Tier 1 biter → Tier 2 başlar (artık batch)
  const queries = useTieredQueries(dateRange, faaliyetDateRange);
  const { aktifData, pasifData, tier1Loading } = useBilancoData(queries);

  // Detect agent errors from tier 1 batch query
  const hasAgentError = !tier1Loading && queries.tier1Batch.isError;

  const handleRetry = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dashboard-tier1-batch'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-tier2-batch'] });
  }, [queryClient]);

  return (
    <>
      <style>{`
        @media (max-width: 1199px) {
          .bilanco-grid { grid-template-columns: 1fr !important; }
          .charts-grid { grid-template-columns: 1fr !important; }
          .faaliyet-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 767px) {
          .charts-grid { grid-template-columns: 1fr !important; }
        }
        .bilanco-row:hover { background: var(--color-hover-bg) !important; }
        .clickable-row:hover { background: var(--color-selected-bg) !important; cursor: pointer; }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <AgentStatusBanner hasError={hasAgentError} onRetry={handleRetry} />

        <BilancoSection
          aktifData={aktifData}
          pasifData={pasifData}
          isLoading={tier1Loading}
          onRowClick={(path) => navigate(path)}
        />

        <ChartsSection
          queries={queries}
          dateFilter={dateFilter}
          onDateFilterChange={setDateFilter}
        />

        <FaaliyetSection
          queries={queries}
          faaliyetFilter={faaliyetFilter}
          onFaaliyetFilterChange={setFaaliyetFilter}
        />
      </div>
    </>
  );
}
